import axios from "axios";

import {
  IMersRegisterTransaction,
  IMersReverseRegistrationTransaction,
  IMersSecuredPartyTransaction,
  IMersTransactionResponse,
  IMersUpdateChangeDataTransaction,
  IMersUpdateChangeStatusTransaction,
  MersModificationTypes,
} from "Features/Vault/VaultInterfaces";

/**
 * Remove Secured Party
 * @param transaction Contains the min
 * @returns the Promise for the request
 */
export function removeSecuredParty(transaction: IMersSecuredPartyTransaction): Promise<IMersTransactionResponse[]> {
  return axios
    .delete(`/api/packages/${transaction.packageId}/mers/registrations/secured-party?min=${transaction.min}`, {
      data: transaction,
    })
    .then((response) => response.data);
}

/**
 * Remove Delegatee for Transfers
 * @param packageId packageId
 * @param min min
 * @returns the Promise for the request
 */
export async function removeDelegateeForTransfer(packageId: string, min: string): Promise<any> {
  return await axios
    .delete(`/api/v1/packages/${packageId}/mers/registrations/delegatee-for-transfer?min=${min}`)
    .then((response) => response.data);
}

/**
 * Remove Subservicer
 * @param packageId packageId
 * @param min min
 * @returns the Promise for the request
 */
export async function removeSubservicer(packageId: string, min: string): Promise<any> {
  return await axios
    .delete(`/api/v1/packages/${packageId}/mers/registrations/subservicer?min=${min}`)
    .then((response) => response.data);
}

export function registerMERSPackage(data: IMersRegisterTransaction): Promise<IMersTransactionResponse[]> {
  return axios.post(`/api/v2/packages/${data.packageId}/mers/registrations`, data).then((response) => response.data);
}

export function reverseRegisterMERSPackage(
  data: IMersReverseRegistrationTransaction
): Promise<IMersTransactionResponse> {
  return axios.delete(`/api/v1/packages/${data.packageId}/mers/registrations`).then((response) => response.data);
}

export function modifyMERSPackage(
  packageId: string,
  min: string,
  type: MersModificationTypes
): Promise<IMersTransactionResponse[]> {
  const payload = {
    type,
  };
  return axios
    .post(`/api/packages/${packageId}/mers/registrations/modification?min=${min}`, payload)
    .then((response) => response.data);
}

export function modificationReversal(packageId: string, min: string): Promise<IMersTransactionResponse[]> {
  return axios
    .delete(`/api/packages/${packageId}/mers/registrations/modification-agreements?min=${min}`)
    .then((response) => response.data);
}

export function noteStatusUpdateMERSPackage(
  updatePackage: IMersUpdateChangeStatusTransaction
): Promise<IMersTransactionResponse[]> {
  return axios
    .patch(`/api/packages/${updatePackage.packageId}/mers/registrations/note?min=${updatePackage.min}`, {
      noteStatus: updatePackage.noteStatus,
    })
    .then((response) => response.data);
}

const locationChangeGuidance = "Location cannot be changed through Change Data. Use Transfer (Location) instead.";

export function rightsholderUpdateMERSPackage(
  updatePackage: IMersUpdateChangeDataTransaction
): Promise<IMersTransactionResponse[]> {
  let propertyKey = "";
  switch (updatePackage.rightsholder.toLowerCase()) {
    case "locationrecipient":
      return Promise.reject(new Error(locationChangeGuidance));
    case "servicingagentrecipient":
      propertyKey = "Delegatee";
      break;
    case "delegateefortransferrecipient":
      propertyKey = "DelegateeForTransfers";
      break;
    case "securedpartyrecipient":
      propertyKey = "SecuredParty";
      break;
    case "securedpartydelegateerecipient":
      propertyKey = "SecuredPartyDelegatee";
      break;
    case "subservicerrecipient":
      propertyKey = "SubServicer";
  }
  const update = {
    [propertyKey]: updatePackage.mersOrgId,
  };
  return axios
    .patch(`/api/packages/${updatePackage.packageId}/mers/registrations/rightsholder?min=${updatePackage.min}`, update)
    .then((response) => response.data)
    .catch((error: unknown) => {
      const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
      const data = response?.data;
      const validationErrors =
        typeof data === "object" && data !== null
          ? (data as { errors?: unknown }).errors
          : undefined;
      const locationErrors = (validationErrors as { Location?: unknown } | null)?.Location;
      const hasLocationValidationError = Array.isArray(validationErrors) && validationErrors.some((error: unknown) => {
        if (typeof error !== "object" || error === null) {
          return false;
        }
        const validationError = error as { propertyName?: unknown; errorMessage?: unknown };
        return validationError.propertyName === "Location" && validationError.errorMessage === locationChangeGuidance;
      });
      if (
        response?.status === 400 &&
        (data === locationChangeGuidance ||
          hasLocationValidationError ||
          (Array.isArray(locationErrors) && locationErrors.includes(locationChangeGuidance)))
      ) {
        throw new Error(locationChangeGuidance);
      }
      throw error;
    });
}
